/** @format */

import { upsertPaper } from "../api/papers";
import { createImport, startImportExecution } from "../api/imports";
import {
	buildPaperId,
	type Brand,
	type CoreRef,
	type DraftQuestionInput,
	type ImportJob,
	type PaperQualification,
} from "@question-bank/shared";

/** The paper-level block chosen on the import page (DR8), used to create the Paper. */
export interface ImportPaper {
	paperCode: string;
	subject: string;
	/** Core qualification reference (code + optional title/uuid) for the paper (QB2-223). */
	qualification: PaperQualification;
	/** Core tier reference (code + optional title/uuid); omitted when no tier (DR11). */
	tier?: CoreRef;
	/** Core series (season) reference (code + optional title/uuid) (DR11). */
	season: CoreRef;
	year: number;
	date: string;
}

/** Upsert the paper an import belongs to and return its deterministic id. */
const upsertImportPaper = async (brand: Brand, paper: ImportPaper): Promise<string> => {
	// `buildPaperId` keys on the Core season CODE, so the derived paper id
	// matches `{brand}--{paperCode}--{seasoncode-year}` (QB2-223).
	const paperId = buildPaperId({
		brand,
		paperCode: paper.paperCode,
		series: { season: paper.season.code, year: paper.year },
	});
	await upsertPaper(brand, paperId, {
		paperCode: paper.paperCode,
		series: { season: paper.season, year: paper.year },
		date: new Date(paper.date) as never,
		subject: paper.subject,
		qualification: paper.qualification,
		...(paper.tier ? { tier: paper.tier } : {}),
		attachments: [],
	});
	return paperId;
};

export const questionImportService = {
	/**
	 * Import a batch: create (or replace) the Paper that owns the batch, then
	 * send every row in one import request for that paper. One import = one
	 * paper (QB2-223, DR8). The paper is created first so the questions it owns
	 * always resolve their paper-level facts. The server stamps `paperId` onto
	 * each row, so the rows go up as they are.
	 */
	async importQuestions(brand: Brand, paper: ImportPaper, rows: DraftQuestionInput[]): Promise<ImportJob> {
		// Create the owning paper first. If this fails the import is not sent.
		const paperId = await upsertImportPaper(brand, paper);

		// `subject` is the value the preview page shows as the paper's Subject.
		return createImport(brand, { source: "csv", paperId, paperCode: paper.paperCode, subject: paper.subject, rows });
	},

	/**
	 * Start a PDF import: create (or replace) the owning paper, record the job
	 * against the uploaded PDF keys, then start its extraction pipeline.
	 */
	async importPdfs(
		brand: Brand,
		paper: ImportPaper,
		sourceKeys: { paper: string; markScheme: string; specification: string },
	): Promise<ImportJob> {
		const paperId = await upsertImportPaper(brand, paper);
		const job = await createImport(brand, {
			source: "pdf",
			paperId,
			paperCode: paper.paperCode,
			subject: paper.subject,
			sourceKeys,
		});
		await startImportExecution(brand, job.id);
		return job;
	},
};
